/* /payslip/:empId/:period — bilingual payslip (earnings, deductions, GOSI detail, net) with print. */
import "./pay.css";
import { useParams } from "react-router-dom";
import { useEmployee, usePayslip, useSettings } from "../../data/hooks";
import { useOverlays } from "../../app/overlays";
import { useI18n, LOOKUP } from "../../i18n";
import { fmtN, gDate, hDate, MONTHS_EN, periodLabel } from "../../lib/format";
import { Alert, Button, ErrorBox, PageHeader, Skeleton } from "../../ui";
import { useRole } from "./components/common";
import { curCode, otPct } from "../../lib/country";

const pct = (part: number, base: number) => (base > 0 ? +((part / base) * 100).toFixed(2) : 0);

export default function Payslip() {
  const { empId = "", period = "" } = useParams();
  const id = Number(empId);
  const { t, L, lang, name } = useI18n();
  const { openPrint } = useOverlays();
  const { role, user, ready } = useRole();
  const validPeriod = /^\d{4}-(0[1-9]|1[0-2])$/.test(period) && Number.isInteger(id) && id > 0;
  const isEmp = role === "employee";
  const forbidden = isEmp && user?.employeeId !== id;
  const enabled = ready && validPeriod && !forbidden;
  const slipQ = usePayslip(id, period, { enabled });
  const empQ = useEmployee(id, { enabled });
  const setQ = useSettings({ enabled: ready && !isEmp });

  const back = isEmp ? { to: "/ess/payslips", label: t("Payslips") } : { to: "/employees/" + id, label: empQ.data ? name(empQ.data) : t("Employee") };
  if (!validPeriod) return <><PageHeader title={t("Payslip")} back={back} /><Alert tone="bad" testId="bad-period" title={L("Invalid payslip address", "رابط القسيمة غير صحيح")} desc={L("Expected /payslip/<employee>/<YYYY-MM>.", "الصيغة المتوقعة /payslip/<الموظف>/<YYYY-MM>.")} /></>;
  if (forbidden) return <><PageHeader title={t("Payslip")} back={back} /><Alert tone="bad" testId="forbidden" title={L("You can only view your own payslips", "يمكنك عرض قسائمك فقط")} /></>;
  if (slipQ.error) return <ErrorBox error={slipQ.error} onRetry={() => slipQ.refetch()} />;
  if (empQ.error) return <ErrorBox error={empQ.error} onRetry={() => empQ.refetch()} />;
  if (!slipQ.data || !empQ.data) return <Skeleton />;

  const p = slipQ.data, e = empQ.data, g = p.gosi;
  const m = Number(period.slice(5, 7)) - 1, y = Number(period.slice(0, 4));
  const payDate = period + "-" + String(setQ.data?.payDay ?? 27).padStart(2, "0");
  const tenant = setQ.data?.tenant;
  const sa = e.nationality === "SA";
  const line = (en: string, ar: string, v: number, tid?: string) => (
    <tr key={en} data-testid={tid}><td>{en} <span className="ar muted" style={{ fontSize: 11.5 }}>{ar}</span></td><td className="end num">{fmtN(v)}</td></tr>
  );

  return (
    <>
      <PageHeader
        back={back}
        title={t("Payslip") + " · " + periodLabel(period, lang)}
        sub={name(e) + " · " + e.code}
        actions={<Button variant="pri" icon="printer" onClick={() => openPrint({ doc: "payslip", emp: id, period })} data-testid="btn-print-payslip">{t("Print")}</Button>}
      />
      <article className="paper printable" dir="ltr" style={{ maxWidth: 860, marginInline: "auto" }} data-testid="payslip">
        <div className="paper-head">
          <div className="row nw" style={{ gap: 12, alignItems: "flex-start" }}>
            <span className="seal">{(tenant?.name ?? "R").slice(0, 1)}</span>
            <div>
              <h2>{tenant?.name ?? "—"}</h2>
              {tenant && <div className="ar" style={{ fontWeight: 600 }}>{tenant.nameAr}</div>}
              {tenant && <div className="muted" style={{ fontSize: 12 }}>CR {tenant.cr} · VAT {tenant.vat} · {tenant.city}</div>}
            </div>
          </div>
          <div style={{ textAlign: "end" }}>
            <div style={{ fontSize: 12 }} className="muted">PAYSLIP · <span className="ar">قسيمة راتب</span></div>
            <div style={{ font: "600 18px var(--font-mono)" }}>{MONTHS_EN[m].toUpperCase()} {y}</div>
            <div className="ar muted" style={{ fontSize: 12 }}>{hDate(payDate)}</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "8px 32px", marginBottom: 18 }}>
          {[
            ["Employee", "الموظف", e.nameEn + " · " + e.nameAr],
            ["Employee ID", "الرقم الوظيفي", e.code],
            [sa ? "National ID" : "Iqama", sa ? "الهوية" : "الإقامة", e.idNumber],
            ["Job title", "المسمى", e.title],
            ["Department", "القسم", LOOKUP.DEPT[e.dept]?.[0] ?? e.dept],
            ["Bank / IBAN", "البنك / الآيبان", e.bank + " · " + e.iban.replace(/\s/g, "").slice(0, 8) + "••••"],
            ["Pay date", "تاريخ الصرف", gDate(payDate, "en")],
            ["GOSI status", "حالة التأمينات", g.regime === "non-saudi" ? "Non-Saudi · OH only" : "Saudi · " + (g.regime === "new" ? "new regime" : "full")],
          ].map(([a, b, c]) => (
            <div key={a} className="bi" style={{ borderBottom: "1px dashed #e6e1d6", padding: "5px 0" }}>
              <span className="muted" style={{ fontSize: 12 }}>{a} · <span className="ar">{b}</span></span>
              <span style={{ fontWeight: 500, textAlign: "end" }}>{c}</span>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 24 }}>
          <table data-testid="earnings">
            <thead><tr><th>Earnings <span className="ar muted">المستحقات</span></th><th className="end">{curCode()}</th></tr></thead>
            <tbody>
              {line("Basic salary", "الراتب الأساسي", p.basic)}
              {line("Housing allowance", "بدل السكن", p.housing)}
              {line("Transport allowance", "بدل النقل", p.transport)}
              {p.other > 0 && line("Other allowance", "بدلات أخرى", p.other)}
              {p.ot > 0 && line("Overtime · " + fmtN(p.otHours, p.otHours % 1 ? 2 : 0) + " h × " + otPct() + "", "عمل إضافي", p.ot, "line-ot")}
              {p.bonus > 0 && line("Bonus", "مكافأة", p.bonus, "line-bonus")}
            </tbody>
            <tfoot><tr className="tot"><td>Total earnings</td><td className="end num" data-testid="total-earnings">{fmtN(p.earnings)}</td></tr></tfoot>
          </table>
          <table data-testid="deductions">
            <thead><tr><th>Deductions <span className="ar muted">الاستقطاعات</span></th><th className="end">{curCode()}</th></tr></thead>
            <tbody>
              {g.empAnn > 0 && line("GOSI annuities " + g.annRate + "%", "المعاشات", g.empAnn, "line-gosi-ann")}
              {g.empSaned > 0 && line("GOSI SANED " + pct(g.empSaned, g.base) + "%", "ساند", g.empSaned)}
              {p.abs > 0 && line("Absence · " + p.absDays + " days", "غياب", p.abs, "line-abs")}
              {p.loan > 0 && line("Loan instalment", "قسط قرض", p.loan, "line-loan")}
              {p.ded > 0 && line("Other deductions", "استقطاعات أخرى", p.ded, "line-ded")}
              {!p.deductions && <tr><td className="muted">No deductions</td><td className="end num">0.00</td></tr>}
            </tbody>
            <tfoot><tr className="tot"><td>Total deductions</td><td className="end num" data-testid="total-deductions">{fmtN(p.deductions)}</td></tr></tfoot>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 20, padding: "16px 18px", background: "#0d6b55", color: "#fff", borderRadius: 10 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>NET PAY · <span className="ar">صافي الراتب</span></div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Paid by bank transfer (WPS) · {e.bank}</div>
          </div>
          <div style={{ font: "600 26px var(--font-mono)" }} data-testid="net-pay">{curCode()} {fmtN(p.net)}</div>
        </div>
        {p.cap.over && <div style={{ marginTop: 12 }}><Alert tone="bad" testId="cap-over" title={L("Deductions exceed 50% of wage (Art. 92)", "الاستقطاعات تتجاوز 50% من الأجر (م 92)")} desc={curCode() + " " + fmtN(p.cap.amount) + " / " + fmtN(p.cap.limit)} /></div>}
        <div style={{ marginTop: 18 }} data-testid="employer-gosi">
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Employer GOSI contributions (not deducted) · <span className="ar">اشتراكات صاحب العمل</span></div>
          <table>
            <tbody>
              {g.erAnn > 0 && line("Annuities " + pct(g.erAnn, g.base) + "%", "المعاشات", g.erAnn)}
              {g.erSaned > 0 && line("SANED " + pct(g.erSaned, g.base) + "%", "ساند", g.erSaned)}
              {line("Occupational hazards " + pct(g.erOH, g.base) + "%", "الأخطار المهنية", g.erOH)}
            </tbody>
            <tfoot><tr className="tot"><td>Employer total · base {curCode()} {fmtN(g.base)}</td><td className="end num" data-testid="employer-total">{fmtN(g.er)}</td></tr></tfoot>
          </table>
        </div>
        <p className="muted" style={{ fontSize: 11, margin: "18px 0 0" }}>This payslip is generated electronically and does not require a signature. · <span className="ar">هذه القسيمة صادرة إلكترونيًا ولا تحتاج إلى توقيع.</span></p>
      </article>
    </>
  );
}
